// @ts-strict-ignore
import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { LMDashboardPanelBaseComponent } from './lm-dashboard-panel-base.component';
import { LandmarkPanel } from '@form/lm-form.model';
import { LMUrlUtilService } from '@services/utils/lm-url-util.service';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { LMModel } from '@form/model/lm-model';
import * as urltemplate from 'url-template';
import { LMTextEvent } from '@field/text';
import { LMButtonType, LMGlobalUtilService } from '@utils/index';
import { LMNavigationService } from '@navigation/lm-navigation.service';
import { DocumentResource } from '@resources/document.resource';
import { LMFormContentTemplateComponent } from '@form/lm-form-content-template.component';
import { LMThemeService } from '@banner/lm-theme.service';
import { LMUrlInfoBuilderService } from '@services/lm-url-info-builder.service';

@Component({
  selector:    'lm-dashboard-panel-form-template',
  templateUrl: './lm-dashboard-panel-form-template.component.html',
  styleUrls:   [ 'lm-dashboard-panel-form.component.scss' ]
})

export class LMDashboardPanelFormTemplateComponent extends LMDashboardPanelBaseComponent implements OnInit {
  @ViewChild('LMFormContentTemplateComponent') lmFormTemplateComponent: LMFormContentTemplateComponent;
  @HostBinding('attr.data-automation-id') public automationId;

  @Output() componentDisplayed = new EventEmitter<any>();

  @Input() model: LMModel; // used by composite forms when displaying panels as dashboard panes
  @Input() specFields: LMSpecFields; // used by composite forms when displaying panels as dashboard panes
  @Input() placeHolder: ViewContainerRef; // used by composite forms when displaying panels as dashboard panes
  @Input() messageRuleView: LMDataView;

  @Input() set panel(panel) {
    this._panel = panel;
    this.build();
  };

  get panel(): LandmarkPanel {
    return this._panel;
  }

  @Input() set fromDataView(dataView) {
    this._dataView = dataView;
    this.build();
  };

  get fromDataView() {
    return this._dataView;
  }

  public formSpec: LMFormSpec;
  public initDataView: LMDataView = null;
  private _dataView: LMDataView;
  private _panel: LandmarkPanel;

  public formUrlInfo: LMFormUrlInfo;
  public overNavigationLink: string;
  public hasOverviewNavigation = false;
  public displayLinkAsText = false;

  constructor(
    private lmUrlUtilService: LMUrlUtilService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmNavigationService: LMNavigationService,
    private lmGlobalUtilService: LMGlobalUtilService,
    protected lmThemeService: LMThemeService,
    protected lmUrlInfoBuilderService: LMUrlInfoBuilderService
  ) {
    super(lmThemeService);
  }

  ngOnInit() {
    this.automationId = `lm-${this.panel.automationId}-dashboardpanel`;
  }

  saveDashboardPanelState(): void {  }

  clearDashboardPanelState(): void {  }

  public onNavigationClick(): void {
    const navName = this.panel.overviewNavigation ? this.panel.overviewNavigation.name : this.panel.busFormPanel.overviewNavigation.name;
    this.runOverviewNavigation(navName);
  }

  onFieldEvent(event: LMFieldEvent): void {
    if ('TextLinkEvent' === event.eventType) {
      const linkEvent: LMTextEvent = event as LMTextEvent;

      if (linkEvent.layoutObject.hasHttpUrl || linkEvent.layoutObject.linkNavigation) {
        this.buildFormTextLinkNavigation(linkEvent.layoutObject);
      } else if (linkEvent.layoutObject.type === LMButtonType.NAVIGATION) {
        this.runNavigation(linkEvent.layoutObject.navigation, linkEvent.layoutObject.showAsPdf);
      } else if (linkEvent.layoutObject.type === LMButtonType.VIEWDOCUMENT) {
        this.openDocumentResource(linkEvent.layoutObject.resourceId);
      }
    }
  }

  build() {
    if (this._panel && this._dataView) {
      this.formUrlInfo = this.lmUrlInfoBuilderService.getFormUrlInfoFromFormSpec(this._panel.formSpec);

      if (this._panel.overviewNavigation || (this._panel.busFormPanel && this._panel.busFormPanel.overviewNavigation)) {
        this.hasOverviewNavigation = true;
        if ((this._panel.overviewNavigation && this._panel.overviewNavigation.label) || (this._panel.busFormPanel.overviewNavigation && this._panel.busFormPanel.overviewNavigation.label)) {
          this.displayLinkAsText = true;
          this.overViewNavigationLabel();
        }
      }
    }
  }

  overViewNavigationLabel() {
    const message = !!this.panel.overviewNavigation ? this.panel.overviewNavigation.label : this._panel.busFormPanel.overviewNavigation.label;
    const dataView = this.fromDataView ? this.fromDataView : this.messageRuleView;
    this.overNavigationLink = this.lmFormatUtilService.formatMessageRule(message, dataView);
  }

  onFormRendered() {
    this.componentDisplayed.emit({ panel: this.panel });
  }

  private runNavigation(navigation: string, showAsPdf: boolean) {
    const dataView = this.fromDataView;

    const compiledTemplateNavUrl = urltemplate.parse(this.formUrlInfo.navigationUrl);
    const navigationUrl = compiledTemplateNavUrl.expand({
      resourceId:     dataView.resourceId,
      navigationName: navigation
    });
    this.lmNavigationService.runNavigation(navigationUrl, undefined, { showAsPdf });
  }

  private runOverviewNavigation(navigationName: string): void {
    const dataView = this.fromDataView;

    const compiledUrlTemplate = urltemplate.parse(this.formUrlInfo.navigationUrl);
    const url = compiledUrlTemplate.expand({ resourceId: dataView.resourceId, navigationName });
    this.lmGlobalUtilService.navigateByUrl(url);
  }

  private buildFormTextLinkNavigation(link: LMButtonLayout) {
    if (link.httpUrl) {
      const dataView = this.fromDataView;

      const option: string = (link.openExternal) ? 'external' : '';
      const url: string = this.lmFormatUtilService.formatMessageRule(link.httpUrl, dataView);

      if (url) {
        this.lmGlobalUtilService.navigate(url, null, option);
      }
    }
  }

  private openDocumentResource(resourceId: string): void {
    const dataView = this.fromDataView;
    const documentResource = new DocumentResource({ resourceId, dataView }, '');
    const url: string = this.lmUrlUtilService.buildUrl({ resource: documentResource });
    this.lmGlobalUtilService.navigate(url, null, 'external');
  }
}
